import React from 'react';
import { Grid as MuiGrid, GridProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { breakpoints, spacing } from '../../../theme/tokens';

interface ResponsiveGridProps extends GridProps {
  spacing?: number | string;
  gutter?: 'small' | 'medium' | 'large';
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

const StyledGrid = styled(MuiGrid)<ResponsiveGridProps>(
  ({ theme, gutter, maxWidth }) => {
    const gutterMap = {
      small: 1,
      medium: 2,
      large: 3,
    };

    const maxWidthMap = {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px',
    };

    return {
      width: '100%',
      maxWidth: maxWidth ? maxWidthMap[maxWidth] : '100%',
      margin: maxWidth ? '0 auto' : '0',
      gap: gutter ? theme.spacing(gutterMap[gutter]) : undefined,
    };
  }
);

export const Grid: React.FC<ResponsiveGridProps> = ({
  children,
  spacing: spacingProp,
  gutter,
  maxWidth,
  ...props
}) => {
  return (
    <StyledGrid
      spacing={spacingProp}
      gutter={gutter}
      maxWidth={maxWidth}
      {...props}
    >
      {children}
    </StyledGrid>
  );
};

// Container component for centering content
interface ContainerProps {
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  padding?: boolean;
  className?: string;
}

const StyledContainer = styled('div')<{ maxWidth?: string; padding?: boolean }>(
  ({ theme, maxWidth, padding }) => {
    const maxWidthMap = {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px',
    };

    return {
      width: '100%',
      maxWidth: maxWidth ? maxWidthMap[maxWidth] : '1280px',
      margin: '0 auto',
      padding: padding ? theme.spacing(2) : 0,
      [theme.breakpoints.up('sm')]: {
        padding: padding ? theme.spacing(3) : 0,
      },
      [theme.breakpoints.up('md')]: {
        padding: padding ? theme.spacing(4) : 0,
      },
    };
  }
);

export const Container: React.FC<ContainerProps> = ({
  children,
  maxWidth = 'xl',
  padding = true,
  className,
  ...props
}) => {
  return (
    <StyledContainer
      maxWidth={maxWidth}
      padding={padding}
      className={className}
      {...props}
    >
      {children}
    </StyledContainer>
  );
};

// Responsive column components for common layouts
export const TwoColumnGrid: React.FC<{
  left: React.ReactNode;
  right: React.ReactNode;
  leftSpan?: number;
  rightSpan?: number;
  spacing?: number;
}> = ({ left, right, leftSpan = 6, rightSpan = 6, spacing = 2 }) => (
  <Grid container spacing={spacing}>
    <Grid item xs={12} md={leftSpan}>
      {left}
    </Grid>
    <Grid item xs={12} md={rightSpan}>
      {right}
    </Grid>
  </Grid>
);

export const ThreeColumnGrid: React.FC<{
  left: React.ReactNode;
  center: React.ReactNode;
  right: React.ReactNode;
  spacing?: number;
}> = ({ left, center, right, spacing = 2 }) => (
  <Grid container spacing={spacing}>
    <Grid item xs={12} md={4}>
      {left}
    </Grid>
    <Grid item xs={12} md={4}>
      {center}
    </Grid>
    <Grid item xs={12} md={4}>
      {right}
    </Grid>
  </Grid>
);

export const FourColumnGrid: React.FC<{
  items: React.ReactNode[];
  spacing?: number;
}> = ({ items, spacing = 2 }) => (
  <Grid container spacing={spacing}>
    {items.map((item, index) => (
      <Grid key={index} item xs={12} sm={6} lg={3}>
        {item}
      </Grid>
    ))}
  </Grid>
);

// Dashboard layout grid for fuel monitoring
export const DashboardGrid: React.FC<{
  sidebar?: React.ReactNode;
  main: React.ReactNode;
  sidebarWidth?: number;
}> = ({ sidebar, main, sidebarWidth = 280 }) => (
  <Grid container sx={{ height: '100vh' }}>
    {sidebar && (
      <Grid
        sx={{
          width: { xs: 0, md: sidebarWidth },
          display: { xs: 'none', md: 'block' },
        }}
      >
        {sidebar}
      </Grid>
    )}
    <Grid sx={{ flex: 1, overflow: 'auto' }}>
      {main}
    </Grid>
  </Grid>
);

export default Grid;